import { Collapse, Stack, styled } from '@ethings/ui-toolkit';
import { ComponentProps, FC, forwardRef, useState, type JSX } from 'react';

interface Props {
  historyDrawerContent: JSX.Element;
  isDrawerOpen: boolean;
  rightDrawerContent?: JSX.Element;
  setIsTransitioning: (isTransitioning: boolean) => void;
  twoWayCollapseContent: JSX.Element;
}

interface StyledCollapseProps extends ComponentProps<typeof Collapse> {
  isDrawerOpen: boolean;
  width: string;
}

const OPEN_WIDTH = '445px !important';
const CLOSED_WIDTH = 75;

const StyledCollapseBase = styled(Collapse, { shouldForwardProp: (prop) => prop !== 'isOpen' })<{ isOpen: boolean; width: string }>(({ theme, isOpen, width }) => ({
  height: '100%',
  minHeight: 0,
  boxShadow: theme.shadows[4],
  scrollbarWidth: 'none',
  zIndex: 500,
  '& >.MuiCollapse-wrapper': {
    justifyContent: 'center',
    '& > .MuiCollapse-wrapperInner': {
      width,
      maxWidth: isOpen ? OPEN_WIDTH : `${CLOSED_WIDTH}px`,
    },
  },
  '::-webkit-scrollbar': {
    display: 'none',
  },
}));

const StyledCollapse = forwardRef<HTMLDivElement, StyledCollapseProps>(
  ({ isDrawerOpen, width, style, className, ...props }, ref) => (
    <StyledCollapseBase
      ref={ref}
      isOpen={isDrawerOpen}
      width={width}
      {...props}
      style={{
        width,
        overflowY: 'auto',
        overflowX: 'inherit',
        ...style,
      }}
      className={`${className || ''} ${!isDrawerOpen ? 'collapsed' : ''}`}
    />
  ),
);

StyledCollapse.displayName = 'StyledCollapse';

const RightDrawerStack = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderLeft: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[2],
  minHeight: 0,
  maxHeight: '100%',
  overflowY: 'hidden',
  scrollbarWidth: 'none',
  width: 360,
  flexShrink: 0,
  zIndex: 450,
  '::-webkit-scrollbar': {
    display: 'none',
  },
}));

export const EventHistoryDesktop: FC<Props> = ({
  historyDrawerContent,
  isDrawerOpen,
  rightDrawerContent,
  setIsTransitioning,
  twoWayCollapseContent,
}) => {
  const [width, setWidth] = useState(isDrawerOpen ? OPEN_WIDTH : 'auto');

  return (
    <Stack direction="row" width={1} height={1} minHeight={0} minWidth={0} overflow="hidden">
      <StyledCollapse
        isDrawerOpen={isDrawerOpen}
        width={width}
        collapsedSize={CLOSED_WIDTH}
        in={isDrawerOpen}
        onEnter={() => setWidth(OPEN_WIDTH)}
        onEntered={() => setIsTransitioning(false)}
        onEntering={() => setIsTransitioning(true)}
        onExited={() => setIsTransitioning(false)}
        onExiting={() => {
          setWidth('auto');
          setIsTransitioning(true);
        }}
        orientation="horizontal"
        timeout={300}
      >
        {historyDrawerContent}
      </StyledCollapse>
      {rightDrawerContent && (
        <RightDrawerStack>
          {rightDrawerContent}
        </RightDrawerStack>
      )}
      <Stack flexGrow={1} minHeight={0} minWidth={0}>
        {twoWayCollapseContent}
      </Stack>
    </Stack>
  );
};
